/*
 * Presento Pronto: Umschlag-Overlay nach dem Hinzufuegen einer Postkarte (snippets/pp-envelope-upsell.liquid).
 *
 * Hoert auf Horizons cartLinesUpdate-Event (Produktseite, Schnellkauf, Sticky-Button). Wurde eine Postkarte
 * erfolgreich hinzugefuegt und liegen weniger Umschlaege als Postkarten im Warenkorb, oeffnet sich das Overlay
 * kurz nach dem Warenkorb-Drawer ueber ihm. Das Hinzufuegen selbst erledigt <pp-card-upsell> (Menge per
 * data-quantity), dadurch aktualisiert sich der Drawer dahinter auf dem normalen Horizon-Weg.
 *
 * "Nein danke", X, Esc oder Klick daneben blenden es fuer den Rest des Besuchs aus.
 */
import { StandardEvents } from '@shopify/events';

const DISMISSED_KEY = 'pp-envelope-upsell-dismissed';
const OPEN_DELAY = 450;

const dialog = /** @type {HTMLDialogElement | null} */ (document.querySelector('dialog[data-pp-envelope-upsell]'));

const wasDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  } catch (error) {
    return false;
  }
};

const markDismissed = () => {
  try {
    sessionStorage.setItem(DISMISSED_KEY, '1');
  } catch (error) {
    /* Nur Komfortfunktion */
  }
};

const track = (name, detail) => {
  try {
    if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
      window.Shopify.analytics.publish(name, detail);
    }
  } catch (error) {
    /* Analytics duerfen nie stoeren */
  }
};

/** "gid://shopify/ProductVariant/123" oder "123" -> 123 */
const toId = (value) => Number(String(value || '').split('/').pop());

if (dialog && typeof dialog.showModal === 'function') {
  const envelopeVariantId = Number(dialog.dataset.variantId);
  const envelopeProductId = Number(dialog.dataset.productId);
  const priceCents = Number(dialog.dataset.priceCents);
  const types = (dialog.dataset.types || 'Postkarte')
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter(Boolean);

  const yes = /** @type {HTMLButtonElement} */ (dialog.querySelector('[data-pp-eu-yes]'));
  const yesLabel = dialog.querySelector('[data-pp-eu-yes] .pp-upsell__add-label');
  const added = dialog.querySelector('[data-pp-eu-added]');
  const forCount = dialog.querySelector('[data-pp-eu-for]');
  const cardImage = /** @type {HTMLImageElement | null} */ (dialog.querySelector('[data-pp-eu-card-img]'));
  const status = dialog.querySelector('.pp-upsell__status');

  let pending = false;
  let accepted = false;

  const isCard = (item) => types.includes(String(item.product_type || '').trim().toLowerCase());

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  };

  const sizedImage = (url) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=240`;
  };

  /** Befuellt das Overlay fuer den aktuellen Warenkorb, gibt die Anzahl fehlender Umschlaege zurueck */
  const prepare = (items, addedItem, currency) => {
    const cards = items.filter(isCard).reduce((sum, item) => sum + item.quantity, 0);
    const envelopes = items
      .filter((item) => item.variant_id === envelopeVariantId || item.product_id === envelopeProductId)
      .reduce((sum, item) => sum + item.quantity, 0);
    const missing = cards - envelopes;
    if (missing < 1) return 0;

    const total = formatMoney(priceCents * missing, currency);
    yes.dataset.quantity = String(missing);
    yes.disabled = false;
    delete yes.dataset.added;
    if (yesLabel) {
      yesLabel.textContent =
        missing === 1 ? `Ja, mit Umschlag · +${total}` : `Ja, ${missing} Umschläge dazu · +${total}`;
    }
    if (added) {
      added.textContent = cards === 1 ? 'Deine Postkarte liegt im Warenkorb' : 'Deine Postkarten liegen im Warenkorb';
    }
    if (forCount) {
      forCount.textContent = missing === 1 ? '' : `für deine ${missing} Postkarten`;
      forCount.hidden = missing === 1;
    }
    if (cardImage && addedItem && addedItem.image) {
      cardImage.src = sizedImage(addedItem.image);
      cardImage.alt = addedItem.product_title || '';
      cardImage.hidden = false;
    }
    if (status) status.textContent = '';
    return missing;
  };

  /* Merken und Messen direkt hier statt im close-Event (feuert in Hintergrund-Tabs teils verspaetet) */
  const close = () => {
    if (!dialog.open) return;
    /* Wer ja gesagt hat, bekommt es bei der naechsten Postkarte wieder angeboten */
    if (!accepted) {
      track('pp_envelope_upsell_decline', {});
      markDismissed();
    }
    dialog.close();
  };

  const open = (missing) => {
    if (dialog.open) return;
    pending = false;
    accepted = false;
    dialog.showModal();
    yes.focus({ preventScroll: true });
    track('pp_envelope_upsell_view', { quantity: missing });
  };

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    /* Sonst schliesst theme-drawer.js den Warenkorb gleich mit */
    event.preventDefault();
    event.stopPropagation();
    close();
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('[data-pp-eu-close]')) close();
  });

  /* <pp-card-upsell> meldet erfolgreiches Hinzufuegen; kurz bestaetigen, dann den Drawer zeigen */
  dialog.addEventListener('pp:upsell:add', () => {
    accepted = true;
    dialog.classList.add('pp-eu--done');
    window.setTimeout(() => {
      close();
      dialog.classList.remove('pp-eu--done');
    }, 1100);
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action !== 'add' || pending || dialog.open || wasDismissed()) return;
    /* Eigene Hinzufuege-Events (Umschlag, Karten-Upsell) ignorieren */
    if (event.target instanceof Element && event.target.closest('pp-card-upsell')) return;

    const variantIds = (event.lines || []).map((line) => toId(line.merchandiseId));
    if (!variantIds.length || !event.promise) return;

    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;
    pending = true;

    event.promise
      .then((result) => {
        const detail = (result && result.detail) || {};
        const items = Array.isArray(detail.items) ? detail.items : [];
        const addedItem = items.find((item) => variantIds.includes(item.variant_id) && isCard(item));

        if (detail.didError || !addedItem || wasDismissed()) {
          pending = false;
          return;
        }

        const missing = prepare(items, addedItem, dialog.dataset.currency);
        if (!missing) {
          pending = false;
          return;
        }

        /* Erst den Warenkorb aufgehen lassen ("liegt drin"), dann das Angebot darueber */
        const show = () => window.setTimeout(() => open(missing), OPEN_DELAY);
        if (sourceModal && sourceModal.open) {
          sourceModal.addEventListener('close', show, { once: true });
        } else {
          show();
        }
      })
      .catch(() => {
        pending = false;
      });
  });
}
